import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import moment from 'moment';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import axios from 'helpers/api';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import { toast } from 'react-toastify';

const Notification = () => {
    const today = moment().format('MM/DD/YYYY');
    const yesterday = moment().subtract(1, 'days').format('MM/DD/YYYY');
    const [fromDate, setFromDate] = useState(yesterday);
    const [toDate, setToDate] = useState(today);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const columns = [
        {
            accessorKey: 'note',
            header: 'Note',
            meta: {
                headerProps: { className: 'text-900' },
                cellProps: { className: 'text-start' }
            }
        },
        {
            accessorKey: 'date',
            header: 'Date',
            meta: {
                headerProps: { className: 'text-900' },
                cellProps: { className: 'text-start' }
            }
        },
        {
            accessorKey: 'details',
            header: 'Details',
            meta: {
                headerProps: { className: 'text-900 text-center' },
                cellProps: { className: 'text-center' }
            },
            cell: ({ row: { original } }) => {
                const iconColor = original.is_read === 1 ? '#9CA3AF' : '#374151';
                return (
                    <FontAwesomeIcon
                        icon={faEye}
                        style={{ color: iconColor, cursor: 'pointer', fontSize: '14px' }}
                        className={`${iconColor} fs-8 cursor-pointer`}
                        onClick={() => handleEyeIcon(original.notification_id, original.notificationId, original.notification_data)}
                    />
                );
            }

        },
    ]

    const handleEyeIcon = async (id, notificationId, notification_data) => {
        const payload = {
            notification_ids: [Number(notificationId)],

        };
        let tokenStr = localStorage.getItem("accessToken");
        const patientDetails = paths.patientDetails;
        const editPrescription = paths.editPrescription;
        try {
            const data = await axios.post(`${process.env.REACT_APP_SERVER_URL}/users/set-notification-as-read`, payload, {
                headers: {
                    'Authorization': `Bearer ${tokenStr}`
                }
            })
            if (data.data.status === true) {
                toast.success('Notification marked as read');
                setTableData(prev =>
                    prev.map(item =>
                        item.notificationId === notificationId
                            ? { ...item, is_read: 1 }
                            : item
                    )
                );
            }
        } catch (err) {
            console.error('Error sending notification click payload:', err);
        }
        if ((Number(id) == 2 || Number(id) == 17) && notification_data) {
            const { facility_id, order_date, order_id, action } = notification_data;

            navigate(
                paths.orderHistoryFacility +
                `?facility=${facility_id}` +
                `&from=${order_date}` +
                `&to=${order_date}` +
                `&order=${order_id}`+
                `&id=${id}`
            );
        }
        if ((Number(id) == 18) && notification_data) {
            const { facility_id, order_date, order_id, } = notification_data;

            navigate(
                paths.orderHistoryFacility +
                `?facility=${facility_id}` +
                `&from=${order_date}` +
                `&to=${order_date}` +
                `&order=${order_id}`+
                `&id=18`
            );
        }
        if (Number(id) == 3 || Number(id) == 4 || Number(id) == 5 || Number(id) == 6) {
            // SharedLiving caregivers have no cycle view (UI-044) — send
            // medication-related taps to the MedPass dashboard instead.
            if (Number(localStorage.getItem("roleId")) === 7) {
                navigate(paths.medpassdashboard);
            } else {
                navigate(paths.cycleDelivery())
            }
        }
        if (Number(id) == 9 && notification_data) {
            navigate(`${patientDetails}/${notification_data.patient_id}`)
        }
        if (Number(id) == 19 && notification_data) {
            navigate(`${patientDetails}/${notification_data.patient_id}` + `?action=${notification_data.action}`)
        }

    }
    useEffect(() => {
        handleSearch();
    }, []);

    const handleDateChange = (setter) => (e) => {
        const formattedDate = moment(e).format('MM/DD/YYYY');
        setter(formattedDate);
    };

    const handleSearch = async () => {
        setLoading(true);
        const payload = {
            from_date: fromDate != 'Invalid date' ? fromDate : '',
            to_date: toDate != 'Invalid date' ? toDate : ''
        };
        let tokenStr = localStorage.getItem("accessToken");
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_SERVER_URL}/users/notifications`, payload, { headers: { 'Authorization': `Bearer ${tokenStr}` } })
            let dataObj = data.data;
            let results = [];
            dataObj.map((item) => {
                let obj = {
                    note: item.message,
                    date: item.created ? moment(item.created).format('MM/DD/YY') : '',
                    notifications_sent_id: item.notifications_sent_id,
                    notificationId: item.id,
                    notification_id: item.notification_id,
                    is_read: item.is_read || 0,
                    _createdTime: item.created ? moment(item.created).valueOf() : 0,
                    notification_data: item.notification_data
                        ? typeof item.notification_data === 'string'
                            ? JSON.parse(item.notification_data)
                            : item.notification_data
                        : null
                }
                results.push(obj);
            })
            results.sort((a, b) => b._createdTime - a._createdTime);

            const cleanedResults = results.map(({ _createdTime, ...rest }) => rest);

            setTableData(cleanedResults);
            setTableData(results);

        } catch (error) {
            console.log('Results:');
        } finally {
            setLoading(false);
        }
    };

    const table = useAdvanceTable({
        data: tableData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });


    return (
        <>
            <Card className="p-3 mb-3">
                <Row>
                    <Col md={4}>
                        <Form.Group controlId="fromDate">
                            <Form.Label>From Date</Form.Label>
                            <DatePicker
                                selected={moment(fromDate, 'MM/DD/YYYY').toDate()}
                                onChange={handleDateChange(setFromDate)}
                                dateFormat="MM/dd/yyyy"
                                className="form-control"
                                placeholderText="MM/DD/YYYY"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="toDate">
                            <Form.Label>To Date</Form.Label>
                            <DatePicker
                                selected={moment(toDate, 'MM/DD/YYYY').toDate()}
                                onChange={handleDateChange(setToDate)}
                                dateFormat="MM/dd/yyyy"
                                className="form-control"
                                placeholderText="MM/DD/YYYY"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                        <Button variant="primary" onClick={handleSearch}>
                            Search
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Card className='p-3 mb-3'>
                <AdvanceTableProvider {...table}>
                    <Row>
                        <Col xs="12" className="mb-1 mt-1">
                            {loading ? <ShimmerUI /> : tableData.length > 0 ? <AdvanceTable
                                headerClassName="bg-200 text-nowrap align-middle text-center"
                                rowClassName="text-nowrap align-middle text-center"
                                tableProps={{
                                    size: 'sm',
                                    striped: true,
                                    className: 'fs-10 mb-0 overflow-hidden'
                                }}
                            /> : <Alert variant='info' className="text-center">No Data Found</Alert>}
                        </Col>
                        <Col className="mt-3">
                            <AdvanceTableFooter
                                rowInfo
                                navButtons
                                rowsPerPageSelection
                            />
                        </Col>
                    </Row>

                </AdvanceTableProvider>
            </Card>
        </>
    );
};

export default Notification;
